// 3rd party
import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';

// Bernie
import { AnalyticsStore } from 'bernie-core';

// Shared UI
import { useLocalization, LocalizedText } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkPrimaryButton, UitkButtonSize } from 'uitk-react-button';
import { UitkCard } from 'uitk-react-cards';
import { UitkTextInput, UitkFakeInput } from 'uitk-react-input';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLoader } from 'uitk-react-loader';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';

// Components
import { CopyButton } from 'components/quicklinks/copy-button';

// Hooks
import { useUrlValidation } from 'hooks/useUrlValidation';

// Models
import { Product, Program, Quicklink } from 'models';
import { LinkAnalyticsMicroMessageContent } from 'models/analytics';
import { LinkType } from 'models/analytics/enums';

// Utils
import {
  getLinkAnalyticsMicroMessage,
  getProductAnalyticsMicroMessage,
  getProgramAnalyticsMicroMessage,
} from '../../util/analytics';
import { getPartnerizeTrackingLink } from '../../util/tracking';

interface CustomQuicklinkProps {
  analytics?: AnalyticsStore;
  activeProgram: any;
  camRef?: string;
  creativeRef?: string;
  program?: Program;
  product?: Product;
  quicklinks?: Quicklink[];
}

enum affhubEvent {
  affhub_copy_code_selected = 'affhub_copy_code.selected',
  affhub_copy_link_selected = 'affhub_copy_link.selected',
  affhub_link_create_selected = 'affhub_link_create.selected',
  affhub_link_create_succeeded = 'affhub_link_create.succeeded',
  affhub_link_create_failed = 'affhub_link_create.failed',
}

type affhubType =
  | affhubEvent.affhub_copy_code_selected
  | affhubEvent.affhub_copy_link_selected
  | affhubEvent.affhub_link_create_selected
  | affhubEvent.affhub_link_create_succeeded
  | affhubEvent.affhub_link_create_failed;

const CustomQuicklink: React.FunctionComponent<CustomQuicklinkProps> = ({
  analytics,
  activeProgram,
  camRef,
  creativeRef,
  program,
  product,
  quicklinks,
}) => {
  const l10n = useLocalization();
  const { title, validUrlPattern } = program;

  const [status, setStatus] = React.useState(null);
  const [url, setUrl] = React.useState({ urlValue: '' });
  const [quicklinkUrl, setQuicklinkUrl] = React.useState('');
  const [quicklinkCode, setQuicklinkCode] = React.useState('');

  const { control, errors, handleSubmit } = useForm({ mode: 'onBlur' });

  const [isValidUrl] = useUrlValidation(
    {
      urlValue: url.urlValue,
    },
    validUrlPattern,
  );

  /* istanbul ignore next */
  const handleClear = () => {
    setUrl({ urlValue: '' });
  };

  const generateQuickLink = (destination) => {
    const { networkTrackingBaseUrl: baseUrl } = program;

    return getPartnerizeTrackingLink({ baseUrl, camRef, creativeRef, destination }) ?? destination;
  };

  const onSubmit = handleSubmit(({ url: submittedURL }) => {
    setStatus('LOADING');

    triggerQuicklinkBuild(submittedURL);
  });

  const triggerQuicklinkBuild = (submittedUrl) => {
    submittedUrl = submittedUrl.replace(/\s+/g, '');
    const urlPattern = new RegExp(/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/);

    if (urlPattern.test(submittedUrl)) {
      submittedUrl = submittedUrl.replace('https://', '').replace('http://', '').replace('www.', '');
      submittedUrl = `https://www.${submittedUrl}`;
    }

    // Dynamic link param will be here
    const newQuicklinkUrl = generateQuickLink(submittedUrl);
    const newQuicklinkCode = `<a href="${newQuicklinkUrl}">${quicklinks[0].text}</a>`;

    setUrl({ urlValue: submittedUrl });
    setQuicklinkUrl(newQuicklinkUrl);
    setQuicklinkCode(newQuicklinkCode);

    setStatus(null);
  };

  const urlValidation = () => {
    if (!isValidUrl) {
      return (
        isValidUrl || (
          <LocalizedText message="product.quicklink.custom.url.error.invalid_url" data={[product.data.validUrl]} />
        )
      );
    }
  };

  const analyticsTrackEvents = (rfrr: affhubType, link?: LinkAnalyticsMicroMessageContent) => {
    if (typeof analytics?.trackEvent === 'function') {
      const microMessages = [
        getProgramAnalyticsMicroMessage(program, activeProgram.programStatus),
        getProductAnalyticsMicroMessage(product),
        getLinkAnalyticsMicroMessage(link),
      ];
      const microMessagesWithoutLink = microMessages.filter((item) => item.schemaName !== 'affiliateHubLink');
      const filterAnalyticsTrackEvent = {
        'affhub_copy_code.selected': () => {
          analytics.trackEvent(rfrr, 'AffHub Copy Code Selected', null, null, true, microMessages);
        },
        'affhub_copy_link.selected': () => {
          analytics.trackEvent(rfrr, 'AffHub Copy Link Selected', null, null, true, microMessages);
        },
        'affhub_link_create.selected': () => {
          analytics.trackEvent(rfrr, 'AffHub Link Create Selected', null, null, true, microMessagesWithoutLink);
        },
        'affhub_link_create.succeeded': () => {
          analytics.trackEvent(rfrr, 'AffHub Link Create Succeeded', null, null, true, microMessages);
        },
        'affhub_link_create.failed': () => {
          analytics.trackEvent(rfrr, 'AffHub Link Create Failed', null, null, true, microMessages);
        },
      };

      filterAnalyticsTrackEvent[rfrr]();
    }
  };

  const buildLinkAnalyticsMicroMessageContent = (linkTargetUrl: string): LinkAnalyticsMicroMessageContent => ({
    linkType: LinkType.CUSTOM,
    linkIndex: 0,
    linkTargetUrl,
  });

  const trackSubmitClick = () => {
    analyticsTrackEvents(affhubEvent.affhub_link_create_selected);
    analyticsTrackEvents(
      isValidUrl ? affhubEvent.affhub_link_create_succeeded : affhubEvent.affhub_link_create_failed,
      buildLinkAnalyticsMicroMessageContent(url.urlValue),
    );
  };

  return (
    <>
      <UitkSpacing margin={{ blockend: 'one' }}>
        <UitkHeading tag="h2" size={4}>
          <LocalizedText message="product.quicklink.custom.title.custom_tracking_link" />
        </UitkHeading>
      </UitkSpacing>

      <UitkSpacing margin={{ blockend: 'six' }}>
        <UitkText>
          <LocalizedText message="product.quicklink.custom.content.paste_url" data={[title]} />
        </UitkText>
      </UitkSpacing>

      <UitkSpacing margin={{ blockend: 'six' }}>
        <UitkCard padded={true}>
          <form name={'CustomQuicklink'} onSubmit={onSubmit} method="POST">
            <UitkLayoutGrid columns={{ small: 1, large: 4 }} space="four">
              <UitkLayoutGridItem colSpan={{ small: 1, large: 1 }}>
                <UitkSpacing padding={{ blockstart: 'three' }}>
                  <UitkText>
                    <LocalizedText message="product.quicklink.custom.url.label.enter_url" data={[title]} />
                  </UitkText>
                </UitkSpacing>
              </UitkLayoutGridItem>

              <UitkLayoutGridItem colSpan={{ small: 1, large: 3 }}>
                <UitkLayoutFlex space="two" className="QuicklinkInputs">
                  <UitkLayoutFlexItem grow={1}>
                    <UitkLayoutFlex direction="column">
                      <Controller
                        control={control}
                        name="url"
                        defaultValue=""
                        type="url"
                        rules={{
                          required: l10n.formatText('product.quicklink.custom.url.error.missing_url'),
                          validate: urlValidation,
                        }}
                        render={({ onChange, onBlur, value, name }) => (
                          <UitkTextInput
                            id="CustomQuicklinkUrlInput"
                            placeholder={l10n.formatText('product.quicklink.custom.url.placeholder')}
                            name={name}
                            label="url"
                            value={url.urlValue}
                            onChange={({ target: { value: newValue } }) => {
                              setUrl({ ...url, urlValue: newValue });
                              onChange(newValue);
                            }}
                            onBlur={onBlur}
                            hasVisibleLabel={false}
                            handleClear={handleClear}
                            hasClearButton={true}
                            data-cy="quicklink-url-field"
                            invalid={errors.url?.message}
                          />
                        )}
                      />
                    </UitkLayoutFlex>
                  </UitkLayoutFlexItem>

                  <UitkLayoutFlexItem alignSelf="start">
                    <UitkPrimaryButton
                      id="CustomQuicklinkSubmitButton"
                      type="submit"
                      className="QuicklinkButton"
                      disabled={errors.length > 0 || status === 'LOADING'}
                      size={UitkButtonSize.LARGE}
                      data-cy="quicklink-submit"
                      onClick={trackSubmitClick}
                    >
                      {status === 'LOADING'
                        ? ''
                        : quicklinkUrl
                        ? l10n.formatText('product.quicklink.custom.form.actions.update')
                        : l10n.formatText('product.quicklink.custom.form.actions.submit')}
                      {status === 'LOADING' && <UitkLoader isSmall={true} isDarkBackground={true} />}
                    </UitkPrimaryButton>
                  </UitkLayoutFlexItem>
                </UitkLayoutFlex>
              </UitkLayoutGridItem>
            </UitkLayoutGrid>

            {quicklinkUrl && (
              <UitkLayoutGrid columns={{ small: 1, large: 4 }} space="four">
                <UitkLayoutGridItem colSpan={{ small: 1, large: 1 }}>
                  <div />
                </UitkLayoutGridItem>

                <UitkLayoutGridItem colSpan={{ small: 1, large: 3 }}>
                  <div>
                    <UitkSpacing margin={{ block: 'four' }}>
                      <UitkHeading tag="h4" size={6}>
                        <LocalizedText message="product.quicklink.custom.content.here_is_your_link" />
                      </UitkHeading>
                    </UitkSpacing>

                    <UitkLayoutGrid columns={{ small: 1, large: 2 }} space="four">
                      <UitkLayoutGridItem>
                        <div>
                          <UitkLayoutFlex space="two" className="QuicklinkInputs" data-stid="copy-custom-link">
                            <UitkFakeInput
                              id="CustomQuicklinkUrl"
                              label={l10n.formatText('product.quicklink.form.link')}
                              value={quicklinkUrl}
                              data-cy="quicklink-url-field"
                              data-stid="copy-custom-link-input"
                            />
                            <CopyButton
                              value={quicklinkUrl}
                              onCopyClick={() =>
                                analyticsTrackEvents(
                                  affhubEvent.affhub_copy_link_selected,
                                  buildLinkAnalyticsMicroMessageContent(quicklinkUrl),
                                )
                              }
                            />
                          </UitkLayoutFlex>
                        </div>
                      </UitkLayoutGridItem>
                      <UitkLayoutGridItem>
                        <div>
                          <UitkLayoutFlex space="two" className="QuicklinkInputs" data-stid="copy-custom-code">
                            <UitkFakeInput
                              id="CustomQuicklinkCode"
                              label={l10n.formatText('product.quicklink.form.code')}
                              value={quicklinkCode}
                              data-cy="quicklink-url-field"
                              data-stid="copy-custom-code-input"
                            />
                            <CopyButton
                              value={quicklinkCode}
                              onCopyClick={() =>
                                analyticsTrackEvents(
                                  affhubEvent.affhub_copy_code_selected,
                                  buildLinkAnalyticsMicroMessageContent(quicklinkCode),
                                )
                              }
                            />
                          </UitkLayoutFlex>
                        </div>
                      </UitkLayoutGridItem>
                    </UitkLayoutGrid>
                  </div>
                </UitkLayoutGridItem>
              </UitkLayoutGrid>
            )}
          </form>
        </UitkCard>
      </UitkSpacing>
    </>
  );
};

export { CustomQuicklink };
